import { createRef, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Auth } from 'aws-amplify';

import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useNavbarContext } from 'context/NavbarContext';
import { useToast } from 'context/ToastContext';
import useFileDownload from 'modules/FileManagerV2/hooks/ui/useFileDownload';
import useUpdateFiles from 'modules/FileManagerV2/hooks/api/useUpdateFiles';
import { handlePageNumberInPagination } from 'common/pagination';
import { useFileMutations } from 'modules/FileManagerV2/hooks/api/useFileMutations';
import { toastConstant } from 'constants/toastmessage';
import { isAllowedFile, FILE_MANAGER_ACCEPT } from 'modules/FileManagerV2/constants/fileManagerConstants';

export function useListView() {
  const {
    files,
    selectedRows,
    selectAll,
    handleSingleCheckboxClick,
    handleSelectAllCheckboxClick,
    pageLimit,
    setPageLimit,
    handleGetFiles,
    onNextPageClick,
    onPrevPageClick,
    fileManagerFilters,
    currentView,
    paginationToken,
    previousToken,
    modals,
    isClientFileManager,
    isGlobalFileManager,
    isCaseFileManager,
    isLeadFileManager,
    isFirmDocuments,
    caseDetails,
    breadCrumbs,
    setSelectedRows,
    onMountUnMount,
    onOperationBreak,
  } = useFileManagerContext();

  const { setShowBulkDeleteModal, setShowBulkMoveModal, setShowShareFileModal, setIsCopy } = modals;
  const [showFileSharingDetailsModal, setShowFileSharingDetailsModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState();

  const { downloadFile, downloadFolder } = useFileDownload();
  const { fileBulkMarkAsRead } = useUpdateFiles();
  const { createFile } = useFileMutations();
  const userContext = useUserDetailsContext();
  const { startUpload } = useNavbarContext();
  const { addToast } = useToast();
  const location = useLocation();
  const isFileManagerRoute = location.pathname === '/file-manager';
  const isVirtualFolder = useMemo(() => breadCrumbs?.some((file) => file?.is_virtual === true), [breadCrumbs]);

  const { selectedFolder, case: caseData } = fileManagerFilters;

  const folderData = useMemo(() => {
    return {
      case_id: isCaseFileManager || isLeadFileManager ? caseDetails?.case_id || undefined : isClientFileManager ? caseData?.id : undefined,
      folderId: breadCrumbs?.at(-1)?.value || selectedFolder,
      folderPath: breadCrumbs?.length > 0 ? breadCrumbs.map((b) => b.value).filter(Boolean) : undefined,
    };
  }, [isCaseFileManager, isLeadFileManager, isClientFileManager, fileManagerFilters, caseDetails, breadCrumbs]);

  const onUploadSuccess = useCallback(
    async (uploaded = []) => {
      if (!uploaded.length) return;

      const user = await Auth.currentAuthenticatedUser();
      const case_id = caseDetails?.case_id ?? (isClientFileManager ? caseData?.id : 'undefined');

      const attachments = uploaded.map((file) => ({
        file_name: file?.file.name,
        display_name: file?.file.name,
        size: file?.file?.size?.toString(),
        file_path: file?.Key,
        file_type: file?.file?.type?.toString(),
        created_by: user.attributes,
        version_id: file?.VersionId || '',
        versions: [],
        complete_path: (file?.file?.webkitRelativePath || file?.file?.path || file?.file?.name)?.replace(/^\/+/, ''),
      }));

      const dataObj = {
        case_id,
        type: 'bulk_upload',
        folder_id: breadCrumbs?.at(-1)?.value || selectedFolder,
        file_folder: attachments,
        not_linked_to_case: false,
        is_lead: isLeadFileManager || false,
      };

      try {
        await createFile({ data: dataObj, is_lead: isLeadFileManager || false });
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.FILES_UPLOADED);
        if (case_id && case_id !== 'undefined') {
          typeof onMountUnMount === 'function' && onMountUnMount('upload-file', 'unmounted', case_id);
        }
      } catch (err) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant.message.PERMISSION_DENIED_TO_UPLOAD_FILE);
        typeof onOperationBreak === 'function' && onOperationBreak();
      }
    },
    [
      createFile,
      addToast,
      pageLimit,
      isClientFileManager,
      fileManagerFilters,
      isLeadFileManager,
      caseDetails,
      onMountUnMount,
      onOperationBreak,
    ]
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles?.length) return;

      const valid = acceptedFiles.filter(isAllowedFile);
      const rejectedCount = acceptedFiles.length - valid.length;

      if (rejectedCount > 0) {
        addToast(
          false,
          toastConstant.toasterType.WARN,
          toastConstant.header.WARNINIG,
          `${rejectedCount} file(s) were skipped — unsupported file type.`
        );
      }
      if (!valid.length) return;

      const caseId = folderData?.case_id;
      if (caseId && caseId !== 'undefined') {
        typeof onMountUnMount === 'function' && onMountUnMount('upload-file', 'mounted', caseId);
      }

      try {
        startUpload(valid, undefined, onUploadSuccess, folderData);
      } catch (err) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant.message.FILE_UPLOAD_FAILED);
        typeof onOperationBreak === 'function' && onOperationBreak();
      }
    },
    [startUpload, folderData, onUploadSuccess, addToast, onMountUnMount, onOperationBreak]
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: !isFileManagerRoute && !isVirtualFolder ? onDrop : () => {},
    noClick: true,
    noKeyboard: true,
    disabled: isFileManagerRoute || isVirtualFolder,
    accept: FILE_MANAGER_ACCEPT,
  });

  const itemsRef = useMemo(
    () =>
      Array(files?.length ? files.length : 0)
        .fill()
        .map(() => createRef()),
    [files]
  );

  const containsFolders = useMemo(() => selectedRows?.some((item) => item?.entry_type === 'folder'), [selectedRows]);

  const getFilePathForPagination = useCallback(() => {
    if (isGlobalFileManager && userContext) return { module: 'file_manager', subModule: '', listName: 'self' };
    if (isCaseFileManager && userContext) return { module: 'case', subModule: '', listName: 'file_manager' };
    if (isLeadFileManager && userContext) return { module: 'leads', subModule: '', listName: 'file_manager' };
    if (isClientFileManager && userContext) return { module: 'client_portals', subModule: 'filemanager', listName: 'self' };
    return undefined;
  }, [isGlobalFileManager, isCaseFileManager, isLeadFileManager, isClientFileManager, userContext]);

  const onPageChange = (event) => {
    const newLimit = event?.value;
    if (newLimit == null) return;
    setPageLimit(newLimit);
    const path = getFilePathForPagination();
    // Pass excludeFromLoading=true and skipReload=true so that saving the pagination
    // preference does not trigger a full loadUserDetails refresh. That refresh re-sets
    // every UserDetailsContext state slice, causing parent components to re-render and
    // fire their own API calls. handlePageNumberInPagination already mutates
    // userContext.firmDetails.pagination_preference in-place before the API call, so
    // the local value is immediately correct without needing a context reload.
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, newLimit, undefined, undefined, true, true);
  };

  const nextChange = useCallback(() => {
    onNextPageClick();
  }, [onNextPageClick]);

  const previousChange = useCallback(() => {
    onPrevPageClick();
  }, [onPrevPageClick]);

  const downloadFiles = useCallback(async () => {
    try {
      const filesToDownload = selectedRows?.filter((item) => item?.entry_type !== 'folder');
      const foldersToDownload = selectedRows?.filter((item) => item?.entry_type === 'folder');
      const resolvedCaseId = isClientFileManager ? fileManagerFilters?.case?.id : caseDetails?.case_id;

      if (filesToDownload?.length) {
        await Promise.all(filesToDownload.map((file) => downloadFile(file, () => handleGetFiles(pageLimit, breadCrumbs?.at(-1)?.value))));
      }
      if (foldersToDownload?.length) {
        await Promise.all(
          foldersToDownload.map((folder) => {
            const caseId = folder?.case_id || resolvedCaseId;
            return downloadFolder(folder, caseId);
          })
        );
      }
      setSelectedRows([]);
    } catch (error) {
      console.error('Error downloading files:', error);
    }
  }, [selectedRows, pageLimit, breadCrumbs, isClientFileManager, fileManagerFilters, caseDetails]);

  const markFilesAsRead = useCallback(async () => {
    try {
      const fileItems = selectedRows?.map((file) => ({ pk: file?.pk, sk: file?.sk }));
      await fileBulkMarkAsRead({ file_items: fileItems, is_read: true });
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Files marked as read successfully');
      // handleGetFiles(pageLimit, breadCrumbs?.at(-1)?.value);
      setSelectedRows([]);
    } catch (error) {
      console.error('Error marking files as read:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to mark files as read');
    }
  }, [selectedRows, pageLimit, breadCrumbs]);

  return {
    // Core Data
    files,
    containsFolders,
    selectedFile,
    caseDetails,
    breadCrumbs,
    fileManagerFilters,
    currentView,
    userContext,

    // Selection
    selectedRows,
    selectAll,
    setSelectedRows,
    handleSingleCheckboxClick,
    handleSelectAllCheckboxClick,

    // Pagination
    pageLimit,
    setPageLimit,
    paginationToken,
    previousToken,
    onPageChange,
    nextChange,
    previousChange,
    getFilePathForPagination,

    // Actions
    handleGetFiles,
    downloadFiles,
    markFilesAsRead,

    // Modals
    modals,
    setShowBulkDeleteModal,
    setShowBulkMoveModal,
    setShowShareFileModal,
    showFileSharingDetailsModal,
    setShowFileSharingDetailsModal,
    setSelectedFile,
    setIsCopy,

    // Route / Manager Flags
    isFileManagerRoute,
    isVirtualFolder,
    isClientFileManager,
    isGlobalFileManager,
    isCaseFileManager,
    isLeadFileManager,
    isFirmDocuments,

    // Drag & Drop
    getRootProps,
    isDragActive,

    // Refs
    itemsRef,
  };
}
